import { ReactNode, useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Tone } from "./model";

/** A section of the fiche: label row (+ optional right-hand action), then its body. */
export function Section({
    id, title, icon: Icon, action, children, className = "", dense = false,
}: {
    id?: string;
    title: string;
    icon?: React.ElementType;
    action?: ReactNode;
    children: ReactNode;
    className?: string;
    dense?: boolean;
}) {
    return (
        <section id={id} className={`scroll-mt-24 rounded-2xl border border-border bg-surface ${dense ? "p-4" : "p-6"} ${className}`}>
            <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="flex items-center gap-2 font-sans text-base font-semibold text-ink">
                    {Icon && <Icon className="h-5 w-5 text-ink-faint" />}
                    {title}
                </h2>
                {action}
            </div>
            {children}
        </section>
    );
}

/** Label above value. Renders nothing when there is no value: an empty field is noise. */
export function Field({ label, children, className = "" }: { label: string; children?: ReactNode; className?: string }) {
    if (children === null || children === undefined || children === "" || children === false) return null;
    return (
        <div className={className}>
            <p className="font-sans text-xs text-ink-muted">{label}</p>
            <div className="mt-0.5 font-sans text-sm text-ink">{children}</div>
        </div>
    );
}

export const TONE_DOT: Record<Tone, string> = {
    success: "bg-success",
    warning: "bg-warning",
    danger: "bg-danger",
    info: "bg-info",
    neutral: "bg-ink-faint",
};

export const TONE_CHIP: Record<Tone, string> = {
    success: "bg-success-subtle text-success",
    warning: "bg-warning-subtle text-warning",
    danger: "bg-danger-subtle text-danger",
    info: "bg-info-subtle text-info",
    neutral: "bg-surface-sunken text-ink-muted",
};

/** Small icon button that copies a value and says so for a moment. */
export function CopyButton({ value, label }: { value: string; label: string }) {
    const [done, setDone] = useState(false);
    useEffect(() => {
        if (!done) return;
        const t = setTimeout(() => setDone(false), 1500);
        return () => clearTimeout(t);
    }, [done]);
    return (
        <button
            type="button"
            onClick={() => navigator.clipboard.writeText(value).then(() => setDone(true)).catch(() => {})}
            aria-label={label}
            title={label}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ink-faint transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
            {done ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
        </button>
    );
}

/** A contact line: icon, value as a link, copy button. */
export function ContactLine({ icon: Icon, href, value, copy = true, external = false }: {
    icon: React.ElementType;
    href: string;
    value: string;
    copy?: boolean;
    external?: boolean;
}) {
    return (
        <div className="flex min-w-0 items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-ink-faint" />
            <a
                href={href}
                {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="min-w-0 flex-1 truncate font-sans text-sm text-ink hover:underline"
            >
                {value}
            </a>
            {copy && <CopyButton value={value} label={`Copier ${value}`} />}
        </div>
    );
}

/** Closes on outside click and Escape - shared by the header's menus. */
export function useDismiss(open: boolean, close: () => void) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && close();
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, close]);
    return ref;
}

export const menuItem =
    "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-sans text-sm text-ink transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
